/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    IProductCollectionResources,
    IProductCollectionViewProps,
    IProductComponentViewProps
} from '@msdyn365-commerce-modules/product-collection';
import {
    ArrayExtensions,
    getPriceForProductSearchResult,
    PriceForProductSearchResultInput
} from '@msdyn365-commerce-modules/retail-actions';
import { INodeProps, Module, Node } from '@msdyn365-commerce-modules/utilities';
import MsDyn365, { IImageSettings } from '@msdyn365-commerce/core';
import { ProductPrice, ProductSearchResult } from '@msdyn365-commerce/retail-proxy';
import * as React from 'react';
import { IProductCollectionProps as IProductCollectionExtensionProps } from '../definition-extensions/product-collection.ext.props.autogenerated';

// Hardcoded image settings for product collection - 1:1 square ratio
// Dimensions are hardcoded, but mode/quality/lazyload will be merged from Site Builder
const baseImageSettings: IImageSettings = {
    viewports: {
        xs: { w: 429, h: 429 },
        sm: { w: 157, h: 157 },
        md: { w: 253, h: 253 },
        lg: { w: 205, h: 205 },
        xl: { w: 430, h: 430 }
    },
    cropFocalRegion: true,
    mode: 5
};

/**
 * Merge hardcoded image settings with Site Builder configuration.
 * @param base - Base image settings with hardcoded dimensions.
 * @param siteBuilder - Image settings from Site Builder (contains mode, quality, lazyload).
 * @returns Merged image settings with dynamic parameters injected into query strings.
 */
const mergeImageSettings = (base: IImageSettings, siteBuilder?: IImageSettings): IImageSettings => {
    if (!siteBuilder) {
        return base;
    }

    const merged: IImageSettings = { ...base };

    // Only override if Site Builder has defined values
    if (siteBuilder.lazyload !== undefined) {
        merged.lazyload = siteBuilder.lazyload;
    }
    if (siteBuilder.mode !== undefined) {
        merged.mode = siteBuilder.mode;
    }
    if (siteBuilder.quality !== undefined) {
        merged.quality = siteBuilder.quality;
    }

    // Build dynamic query strings for each viewport with quality and mode
    const quality = merged.quality ?? 80;
    const mode = merged.mode ?? 5;

    if (merged.viewports) {
        Object.keys(merged.viewports).forEach(viewport => {
            const vp = merged.viewports[viewport as keyof typeof merged.viewports];
            if (vp && vp.w && vp.h) {
                vp.q = `w=${vp.w}&h=${vp.h}&q=${quality}&m=${mode}&f=webp&cropfocalregion=true`;
            }
        });
    }

    return merged;
};

const getProductsFromViewItems = (items?: IProductComponentViewProps[]): ProductSearchResult[] => {
    if (!ArrayExtensions.hasElements(items)) {
        return [];
    }

    return items
        .map(item => {
            const productElement = item.productComponent as React.ReactElement<{ data?: { product?: ProductSearchResult } }>;
            return productElement?.props?.data?.product;
        })
        .filter((product): product is ProductSearchResult => product !== undefined);
};

const ProductCollectionView: React.FC<IProductCollectionViewProps & IProductCollectionExtensionProps<{}>> = props => {
    const shouldDisablePrices = Boolean(props.config.disablePrices);
    const shouldUseAffiliatePricing =
        !!props.config.enableAffiliationBasedPricing && props.context.actionContext.requestContext.user.isAuthenticated;

    const {
        heading,
        ProductCollectionContainer,
        products,
        SingleSlideCarouselComponentProps,
        GridComponentProps,
        isCarousel,
        seeAllButton,
        resources
    } = props;

    const productSearchResults = React.useMemo(() => getProductsFromViewItems(products), [products]);
    const [productPrices, setProductPrices] = React.useState<Map<number, ProductPrice>>(new Map());
    const [isAffiliatePricingLoading, setIsAffiliatePricingLoading] = React.useState<boolean>(shouldUseAffiliatePricing);

    React.useEffect(() => {
        const fetchAffiliatePrices = async (): Promise<void> => {
            if (!shouldUseAffiliatePricing || !MsDyn365.isBrowser) {
                setIsAffiliatePricingLoading(false);
                return;
            }

            if (!ArrayExtensions.hasElements(productSearchResults)) {
                setProductPrices(new Map());
                setIsAffiliatePricingLoading(false);
                return;
            }

            setIsAffiliatePricingLoading(true);

            try {
                const updatedProducts = await getPriceForProductSearchResult(
                    new PriceForProductSearchResultInput(productSearchResults),
                    props.context.actionContext
                );

                const pricesMap = new Map<number, ProductPrice>();
                updatedProducts.forEach(product => {
                    pricesMap.set(product.RecordId, {
                        BasePrice: product.BasePrice,
                        AdjustedPrice: product.Price,
                        CustomerContextualPrice: product.Price
                    });
                });

                setProductPrices(pricesMap);
            } catch (error) {
                setProductPrices(new Map());
                // eslint-disable-next-line no-console -- Log error for debugging
                console.error('Error fetching affiliate prices for product collection:', error);
            } finally {
                setIsAffiliatePricingLoading(false);
            }
        };

        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- Async operation for affiliate price fetching
        fetchAffiliatePrices();
    }, [productSearchResults, props.context.actionContext, shouldUseAffiliatePricing]);

    const showAffiliatePriceSkeleton = shouldUseAffiliatePricing && isAffiliatePricingLoading;

    if (products) {
        return (
            <Module {...ProductCollectionContainer}>
                {heading}
                {seeAllButton}
                {isCarousel
                    ? _renderCarousel(
                          SingleSlideCarouselComponentProps,
                          products,
                          props.config.imageSettings
                              ? mergeImageSettings(baseImageSettings, props.config.imageSettings)
                              : baseImageSettings,
                          shouldDisablePrices,
                          productPrices,
                          showAffiliatePriceSkeleton
                      )
                    : _renderGrid(
                          GridComponentProps,
                          products,
                          props.config.imageSettings
                              ? mergeImageSettings(baseImageSettings, props.config.imageSettings)
                              : baseImageSettings,
                          shouldDisablePrices,
                          productPrices,
                          showAffiliatePriceSkeleton
                      )}
            </Module>
        );
    }

    // If no products are retrieved and the list type is promotional, still render the module to keep the entry of promotion page
    if (props.config.productCollection.listType === 'promotional') {
        return (
            <Module {...ProductCollectionContainer}>
                {heading}
                {seeAllButton}
                {_renderNoProduct(resources)}
            </Module>
        );
    }
    props.context.telemetry.error('Product collection content is empty, module wont render.');
    return null;
};

const _renderCarousel = (
    carouselContainer: INodeProps,
    items: IProductComponentViewProps[],
    imageSettings: IImageSettings,
    disablePrices: boolean,
    productPrices: Map<number, ProductPrice>,
    showAffiliatePriceSkeleton: boolean
): JSX.Element => {
    return (
        <Node {...carouselContainer}>
            {items && items.map(item => _renderProduct(item, imageSettings, disablePrices, productPrices, showAffiliatePriceSkeleton))}
        </Node>
    );
};

const _renderGrid = (
    gridContainer: INodeProps,
    items: IProductComponentViewProps[],
    imageSettings: IImageSettings,
    disablePrices: boolean,
    productPrices: Map<number, ProductPrice>,
    showAffiliatePriceSkeleton: boolean
): JSX.Element => {
    return (
        <Node {...gridContainer}>
            {items && items.map(item => _renderProduct(item, imageSettings, disablePrices, productPrices, showAffiliatePriceSkeleton))}
        </Node>
    );
};

const _renderNoProduct = (resources: IProductCollectionResources): JSX.Element => {
    return (
        <Node className={''}>
            <p>{resources.noProductToRenderText}</p>
        </Node>
    );
};

const _renderProduct = (
    productView: IProductComponentViewProps,
    imageSettings: IImageSettings,
    disablePrices: boolean,
    productPrices: Map<number, ProductPrice>,
    showAffiliatePriceSkeleton: boolean
): JSX.Element => {
    const { ProductContainer, productComponent } = productView;
    const productElement = productComponent as React.ReactElement<any>;
    const existingData = (productElement.props as { data?: { product?: ProductSearchResult; productPrice?: ProductPrice } })?.data;
    const product = existingData?.product;
    const existingProductPrice = existingData?.productPrice;
    const affiliateProductPrice = product ? productPrices.get(product.RecordId) : undefined;

    // Inject image settings and price visibility controls into each product card.
    const enhancedProductComponent = React.cloneElement(productElement, {
        imageSettings,
        hidePrice: disablePrices,
        hideUnitPrice: disablePrices,
        showPriceSkeleton: !disablePrices && showAffiliatePriceSkeleton,
        data: existingData
            ? {
                  ...existingData,
                  productPrice: affiliateProductPrice ?? existingProductPrice
              }
            : existingData
    });

    return <Node {...ProductContainer}>{enhancedProductComponent}</Node>;
};

export default ProductCollectionView;
